import React, { useEffect, useState } from 'react';
import {
  Grid,
  Button,
  Box,
  Card,
  Typography,
  IconButton,
  Stack,
  Switch,
  Chip,
} from '@mui/material';
import { LeadFormActions } from '../../../Services/_actions';
import MoveToStatusSchedularManager from './MoveToStatusSchedular.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useNavigate, useParams } from 'react-router';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ModalForms from '../../Shared/Modals/index.js';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import { convertToReadableFormat } from '../../../utils/helper.jsx';
import { BackButton } from '../../Shared/BackButton/index.jsx';

const CreateEditSchedular: React.FC = () => {
  const actions = LeadFormActions();
  const { showSnackBar } = useSnackBar();
  const { campaignId, leadFormId } = useParams();
  const [schedularData, setSchedularData] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const initialFormData = {
    campaign_id: campaignId,
    lead_form_id: leadFormId,
    current_status_id: '',
    new_status_id: '',
    trigger_time: '',
    active: true,
    action: 'update_status',
    description: '',
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isEditMode, setIsEditMode] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedSchedulerId, setSelectedSchedulerId] = useState(null);
  const navigate = useNavigate();

  const getSchedularList = async () => {
    try {
      const res = await actions.getLeadScheduler(leadFormId);
      if (res?.status === 200) {
        setSchedularData(res.data);
      }
    } catch (err) {
      showSnackBar('Failed to load schedular list.', 'error');
    }
  };

  const buildPayload = (formData: any) => {
    if (formData?.action === 'update_status') {
      return {
        action: 'update_status',
        scheduler_id: formData?.scheduler_id,
        campaign_id: formData?.campaign_id,
        lead_form_id: formData?.lead_form_id,
        trigger_delay: formData?.trigger_delay,
        trigger_delay_unit: formData?.trigger_delay_unit,
        trigger_time: `${formData?.trigger_delay ?? ''} ${formData?.trigger_delay_unit ?? ''}`,
        currentStatus: formData?.currentStatus,
        nextStatus: formData?.nextStatus,
        description: formData?.description,
        active: formData?.active,
      };
    }

    if (formData?.action === 'send_lead_summary') {
      return {
        action: 'send_lead_summary',
        scheduler_id: formData?.scheduler_id,
        campaign_id: formData?.campaign_id,
        lead_form_id: formData?.lead_form_id,
        trigger_time: formData?.frequency === 'Weekly' ? '1 week' : formData?.frequency,
        schedule_time: formData?.sendTime,
        timezone: 'Asia/Kolkata',
        notification_config: {
          template_id: formData?.template?.name ?? '',
          bot_number: formData?.template?.bot_number ?? '',
          min_open_leads_threshold: 1,
        },
      };
    }
    return {};
  };

  const createScheduler = async () => {
    try {
      // const updatedFormData = {
      //   ...formData,
      //   trigger_time: `${formData?.trigger_delay ?? ''} ${formData?.trigger_delay_unit ?? ''}`,
      // };
      const updatedFormData = buildPayload(formData);
      const res = await actions.createLeadScheduler(updatedFormData);
      if (res?.status === 200) {
        showSnackBar('Scheduler created successfully', 'success');
        setShowCreate(false);
        setSchedularData((prev) => ({
          ...prev,
          schedulers: [...(prev.schedulers || []), res?.data?.data?.scheduler],
        }));
      } else {
        const errorMsg = res?.data?.data?.general_error?.message ?? 'Failed to create scheduler.';
        showSnackBar(errorMsg, 'error');
      }
    } catch (err) {
      showSnackBar(JSON.stringify(err) ?? 'Failed to create scheduler.', 'error');
    }
  };

  const updateScheduler = async () => {
    try {
      // const updatedFormData = {
      //   ...formData,
      //   trigger_time: `${formData?.trigger_delay ?? ''} ${formData?.trigger_delay_unit ?? ''}`,
      // };
      const updatedFormData = buildPayload(formData);
      const res = await actions.updateLeadScheduler(updatedFormData);
      if (res?.status === 200) {
        showSnackBar('Scheduler updated successfully', 'success');
        setShowCreate(false);
        setIsEditMode(false);
        setSchedularData((prev) => ({
          ...prev,
          schedulers: prev?.schedulers?.map((sch) =>
            sch.scheduler_id === updatedFormData?.scheduler_id ? res?.data?.data?.scheduler : sch
          ),
        }));
      } else {
        showSnackBar(
          res?.data?.data?.general_error?.message || 'Failed to update scheduler.',
          'error'
        );
      }
    } catch (err) {
      showSnackBar(JSON.stringify(err) ?? 'Failed to update scheduler.', 'error');
    }
  };

  const handleConfirmDelete = async (confirmed) => {
    setConfirmOpen(false);

    if (confirmed && selectedSchedulerId) {
      try {
        const res = await actions.deleteLeadScheduler(selectedSchedulerId);
        if (res?.status === 200) {
          showSnackBar('Scheduler deleted successfully', 'success');
          //   getSchedularList();
          setSchedularData((prev) => ({
            ...prev,
            schedulers: prev.schedulers.filter((sch) => sch.scheduler_id !== selectedSchedulerId),
          }));
        } else {
          showSnackBar(
            res?.data?.data?.general_error?.message ?? 'Failed to delete scheduler.',
            'error'
          );
        }
      } catch (err) {
        showSnackBar(JSON.stringify(err) ?? 'Failed to delete scheduler.', 'error');
      }
    }
    setSelectedSchedulerId(null);
  };

  // const handleEdit = (item) => {
  //   const [delay, unit] = item.trigger_time?.split(' ') ?? [];

  //   setFormData({
  //     ...item,
  //         scheduler_id: item.scheduler_id,
  //     trigger_delay: delay,
  //     trigger_delay_unit: unit,
  //   });
  //   setIsEditMode(true);
  //   setShowCreate(true);
  // };

  const handleEdit = (item) => {
    if (item.action === 'update_status') {
      const [delay, unit] = item.trigger_time?.split(' ') ?? [];
      setFormData({
        ...item,
        scheduler_id: item.scheduler_id,
        trigger_delay: delay,
        trigger_delay_unit: unit,
      });
    } else if (item.action === 'send_lead_summary') {
      // parse trigger_time e.g. "1 week"
      const [value, unit] = item.trigger_time?.split(' ') ?? [];

      setFormData({
        ...item,
        scheduler_id: item.scheduler_id,
        action: 'send_lead_summary',
        reportIntervalValue: value,
        reportIntervalUnit: unit,
        frequency: item.trigger_time,
        sendTime: item.schedule_time,
        template: {
          name: item?.notification_config?.template_id ?? '',
          bot_number: item?.notification_config?.bot_number ?? '',
        },
        active: item.active,
      });
    }

    setIsEditMode(true);
    setShowCreate(true);
  };

  const handleDelete = (id) => {
    setSelectedSchedulerId(id);
    setConfirmOpen(true);
  };

  useEffect(() => {
    getSchedularList();
  }, []);

  const handleToggleActive = async (item, active) => {
    console.log(item, active);
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Schedulers' },
  ];

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        padding: '5px',
        paddingBottom: '15px',
      }}
    >
      {/* <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      /> */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Schedulers
        </Typography>
        <Box sx={{ display: 'flex', gap: '4px' }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              setFormData(initialFormData);
              setShowCreate(true);
            }}
          >
            Create
          </Button>
          <BackButton onClick={() => navigate(-1)} />
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ p: { xs: 1, sm: 2 } }}>
        {schedularData?.schedulers?.length > 0 &&
          schedularData?.schedulers?.map((sch, i) => (
            <Grid item xs={12} sm={6} md={3} key={sch.scheduler_id || i}>
              <Card
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: sch.active ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                    borderColor: sch.active ? '#3b82f6' : '#9ca3af',
                  },
                }}
              >
                {/* Card Content */}
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        color: '#111827',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.2,
                      }}
                    >
                      {sch?.trigger_time}
                    </Typography>

                    {/* Action Buttons */}
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(sch)}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 1.5,
                          '&:hover': {
                            backgroundColor: '#e2e8f0',
                            borderColor: '#cbd5e1',
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14, color: '#64748b' }} />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => handleDelete(sch?.scheduler_id)}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 1.5,
                          '&:hover': {
                            backgroundColor: '#fee2e2',
                            borderColor: '#fca5a5',
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Action Section */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Action
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: '#374151',
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      {convertToReadableFormat(sch?.action)}
                    </Typography>
                  </Box>

                  {/* Status Transition */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Status Transition
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontSize: '0.8rem',
                          color: '#374151',
                          fontWeight: 500,
                        }}
                      >
                        {sch?.currentStatus?.status_name || 'N/A'}
                      </Box>

                      <Typography sx={{ color: '#080808ff', fontSize: '1rem' }}>→</Typography>

                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          backgroundColor: sch.active ? '#eff6ff' : '#f9fafb',
                          borderRadius: '6px',
                          border: sch.active ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                          fontSize: '0.8rem',
                          color: sch.active ? '#1d4ed8' : '#374151',
                          fontWeight: 500,
                        }}
                      >
                        {sch?.nextStatus?.status_name || 'N/A'}
                      </Box>
                    </Box>
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      pt: 1,
                      borderTop: '1px solid #f3f4f6',
                      mt: 'auto',
                    }}
                  >
                    {/* Active/Inactive Switch */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarTodayIcon
                          sx={{
                            fontSize: 14,
                            color: '#9ca3af',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            fontWeight: 500,
                          }}
                        >
                          {sch?.created_at
                            ? new Date(sch.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Chip
                        label={sch.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={sch.active ? 'success' : 'error'}
                        variant="filled"
                      />

                      {/* <Box display="flex" alignItems="center" gap={1}>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: sch.active ? '#3b82f6' : '#9ca3af',
                          fontWeight: 600,
                        }}
                      >
                        {sch.active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Switch
                        checked={sch.active}
                        onChange={(e) => handleToggleActive(sch, e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                            '&:hover': {
                              backgroundColor: 'rgba(59, 130, 246, 0.08)',
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: '#d1d5db',
                          },
                        }}
                      />
                    </Box> */}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
      </Grid>
      {(!schedularData?.schedulers || schedularData?.schedulers.length === 0) && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Create Your First Scheduler
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              setFormData(initialFormData);
              setShowCreate(true);
            }}
          >
            Create Scheduler
          </Button>
        </Box>
      )}
      <ModalForms
        modalHeader={isEditMode ? 'Edit Scheduler' : 'Create Scheduler'}
        show={showCreate}
        handleClose={() => {
          setShowCreate(false);
          setIsEditMode(false);
        }}
        modalBody={<MoveToStatusSchedularManager formData={formData} setFormData={setFormData} />}
        size="sm"
        modalFooter={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={isEditMode ? updateScheduler : createScheduler}
            >
              {isEditMode ? 'Update' : 'Submit'}
            </Button>
          </Box>
        }
      />
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Scheduler?',
          text: 'Are you sure you want to delete this scheduler?',
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};

export default CreateEditSchedular;
